// deployment_class: customer_env_allowed
/**
 * useRouteFocus — moves focus to the new route's primary heading on every
 * pathname change so screen readers announce the new page. Design System
 * spec §9.4 (Focus management — focus moves to new page's h1 on route
 * change).
 *
 * Strategy:
 *   - Listen to pathname changes via useLocation()
 *   - On change (not on initial mount), find the first <h1> inside the
 *     <main id="main-content"> landmark and call .focus() on it.
 *   - The heading must be focusable; we add `tabIndex={-1}` on h1's via
 *     the design system convention (route-level h1's render with
 *     tabIndex={-1} so this hook can focus them programmatically without
 *     making them part of the tab order).
 *   - If no h1 found, fall back to focusing the <main> landmark itself
 *     (also given tabIndex={-1} in App.tsx).
 *
 * Mount this once in the App shell. It is intentionally a no-op on the
 * very first mount — initial page load doesn't need a route-change
 * announcement (the page <title> change is enough).
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export function useRouteFocus(): void {
  const location = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Defer to the next microtask so the new route's content has rendered.
    const id = window.requestAnimationFrame(() => {
      const main = document.getElementById('main-content');
      if (!main) return;
      const h1 = main.querySelector<HTMLElement>('h1');
      const target = h1 ?? main;
      // Ensure focusable; safe to mutate tabIndex if missing.
      if (target.tabIndex === undefined || target.tabIndex < 0) {
        target.tabIndex = -1;
      }
      target.focus({ preventScroll: false });
    });
    return () => window.cancelAnimationFrame(id);
  }, [location.pathname]);
}
